import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '../../types/supabase'; // Assumindo que os tipos gerados estão aqui
import { calculateLevel } from './gamificationUtils';
import { XpHistoryDetails, XpAction, ProcessActionResult, XpHistoryEntry, UserXpData, UserMissionRow, MissionRow, ActiveCooldownInfo, UserMissionUpdateInfo } from './types';
import { supabase } from '../supabase'; // Adicionando import
import { checkAndAwardAllPossibleBadges, awardBadgeToUser } from './badgeService';
import { 
  getActiveEventMultiplier, 
  processUserActionForMissions, // Renomeado de updateUserMissionProgress para o nome real da função
  generateUserMissions
} from './missionService'; 
import { XpActionKey } from './xpConfig';
import { registerStreakActivity, getUserStreakXpMultiplier } from './streakService';

// Lista de ações qualificadas para streak
const STREAK_QUALIFIED_ACTIONS = [
  'record_game_session',
  'add_game_to_collection',
  'add_game_to_wishlist',
  'rate_game',
  'login',
];

/**
 * Adiciona XP a um usuário, registra no histórico e atualiza o nível/XP total.
 * Esta função deve ser chamada dentro de um contexto seguro (ex: Edge Function ou backend).
 * Assume que uma linha para o userId já existe na tabela user_xp (criada por trigger).
 */
export async function addUserXP(
    supabase: SupabaseClient<Database>,
    userId: string,
    xpToAdd: number,
    sourceType: string,
    sourceId?: string,
    actionId?: string,
    details?: XpHistoryDetails
): Promise<{ success: boolean; error?: any; newTotalXp?: number; newLevel?: number }> {
    if (!userId || typeof xpToAdd !== 'number') {
        console.warn('[addUserXP] Tentativa de adicionar XP inválida:', { userId, xpToAdd });
        return { success: false, error: 'User ID ou XP inválido' };
    }

    // 1. Chamar a RPC para atomicamente inserir/atualizar e incrementar XP
    // A RPC 'upsert_and_increment_xp_atomic' irá:
    // - Inserir uma nova linha em user_xp se o usuário não existir, com total_xp = xpToAdd e current_level = 1.
    // - Ou atualizar a linha existente, incrementando total_xp por xpToAdd.
    // - Retornar o novo total_xp.
    const { data: rpcResultTotalXp, error: rpcError } = await supabase.rpc('upsert_and_increment_xp_atomic', {
        p_user_id: userId,
        p_xp_to_add: xpToAdd,
    });

    if (rpcError || rpcResultTotalXp === null || rpcResultTotalXp === undefined) {
        console.error(`[addUserXP] Erro ao chamar RPC upsert_and_increment_xp_atomic para ${userId} (XP: ${xpToAdd}) ou RPC retornou null/undefined:`, rpcError);
        // Se a RPC falhar, pode ser que ela não exista, ou haja um problema de permissão,
        // ou a lógica interna da RPC falhou.
        return { success: false, error: rpcError?.message || 'Falha ao adicionar/atualizar XP via RPC.' };
    }

    const newTotalXp = rpcResultTotalXp as number;

    // 2. Calcular o novo nível com base no newTotalXp retornado pela RPC
    const newLevel = calculateLevel(newTotalXp);

    // 3. Atualizar o nível na tabela user_xp
    //    A RPC já atualizou last_updated_at ao modificar total_xp (ou ao inserir).
    //    Este update de nível também atualiza last_updated_at.
    const { error: updateLevelError } = await supabase
        .from('user_xp')
        .update({ current_level: newLevel, last_updated_at: new Date().toISOString() })
        .eq('user_id', userId);

    if (updateLevelError) {
        // Logar o erro, mas considerar a operação de XP um sucesso, pois o total_xp está correto.
        // O nível pode estar temporariamente dessincronizado se este update falhar, mas é menos crítico que o XP total.
        console.warn(`[addUserXP] XP total para ${userId} atualizado para ${newTotalXp}, mas falha ao atualizar nível para ${newLevel}:`, updateLevelError);
    }

    // 4. Registrar no histórico (APÓS sucesso da atualização do XP)
    const { error: historyError } = await supabase.from('xp_history').insert({
        user_id: userId,
        amount: xpToAdd,
        action_id: actionId, // Passar o ID da ação da tabela xp_actions, se aplicável
        source_type: sourceType,
        source_id: sourceId,
        details: details,
        // earned_at é DEFAULT now() na tabela
    });

    if (historyError) {
        // Logar erro no histórico, mas considerar a operação principal um sucesso
        // já que o XP foi atualizado. Poderia adicionar lógica de compensação se necessário.
        console.error(`[addUserXP] Erro ao inserir no histórico para ${userId}, mas XP (${xpToAdd}) foi adicionado. Novo total: ${newTotalXp}. Erro histórico:`, historyError);
    }

    // console.log(`[addUserXP] ${xpToAdd} XP adicionado a ${userId}. Novo total: ${newTotalXp}, Nível: ${newLevel}`);
    return { success: true, newTotalXp, newLevel };
}

// --- Nova Função: processAction ---

interface ProcessActionArgs {
  supabase: SupabaseClient<Database>;
  userId: string;
  actionKey: XpActionKey;
  sourceId?: string; // ID da entidade relacionada (post_id, game_id)
  details?: XpHistoryDetails;
}

/**
 * Processa uma ação de gamificação específica para um usuário.
 * Busca a ação, verifica se está ativa, chama addUserXP e verifica badges.
 * Retorna um objeto ProcessActionResult.
 */
export async function processAction({
  supabase,
  userId,
  actionKey,
  sourceId,
  details
}: ProcessActionArgs): Promise<ProcessActionResult> {
  // Log para rastrear chamadas a processAction
  console.log(`[GamificationService:processAction] INÍCIO DA EXECUÇÃO para actionKey: ${actionKey}, userId: ${userId}, sourceId: ${sourceId}`);

  // 1. Buscar os detalhes da ação (xp_value, is_active, etc.)
  const { data: actionData, error: actionError } = await supabase
    .from('xp_actions')
    .select('id, xp_value, is_active, cooldown_minutes, max_daily') // Seleciona todos os campos relevantes
    .eq('action_key', actionKey)
    .maybeSingle(); // Usar maybeSingle caso a action_key seja inválida

  if (actionError) {
    console.error(`[processAction] Erro ao buscar ação ${actionKey}:`, actionError);
    return { success: false, error: actionError.message };
  }

  if (!actionData) {
    console.warn(`[processAction] Ação não encontrada: ${actionKey}`);
    return { success: false, error: `Ação inválida: ${actionKey}` };
  }

  // 2. Verificar se a ação está ativa
  if (!actionData.is_active) {
    console.log(`[processAction] Ação ${actionKey} está inativa.`);
    return { success: false, error: 'Ação inativa' };
  }

  // --- NOVO: Verificação Anti-Abuso para Ações Idênticas Repetidas ---
  // Impede que a mesma action_key para o mesmo source_id seja repetida em um curto intervalo.
  const DUPLICATE_ACTION_THRESHOLD_MINUTES = 5; // Definir o intervalo em minutos
  if (actionData.id && sourceId) { // A verificação só faz sentido se tivermos um action_id e um source_id
    try {
      const thresholdTime = new Date(Date.now() - DUPLICATE_ACTION_THRESHOLD_MINUTES * 60 * 1000);
      const { count: duplicateCount, error: duplicateCheckError } = await supabase
        .from('xp_history')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('action_id', actionData.id) // Usar o ID da ação da tabela xp_actions
        .eq('source_id', sourceId)       // Verificar a mesma entidade de origem
        .gte('earned_at', thresholdTime.toISOString());

      if (duplicateCheckError) {
        console.error(`[processAction] Erro ao verificar ação duplicada para ${actionKey} em source ${sourceId}:`, duplicateCheckError);
        // Não bloquear a ação por um erro interno aqui, mas logar.
      } else if (typeof duplicateCount === 'number' && duplicateCount > 0) {
        console.log(`[processAction] Ação ${actionKey} repetida para source ${sourceId} pelo usuário ${userId} dentro do limiar de ${DUPLICATE_ACTION_THRESHOLD_MINUTES} minutos.`);
        return {
          success: false,
          error: `Você já realizou esta ação para este item recentemente. Tente novamente mais tarde.`,
          restriction: 'duplicate_action_on_source',
          details: { thresholdMinutes: DUPLICATE_ACTION_THRESHOLD_MINUTES }
        };
      }
    } catch (error) {
      console.error(`[processAction] Exceção ao verificar ação duplicada para ${actionKey} em source ${sourceId}:`, error);
      // Não bloquear a ação por um erro interno aqui.
    }
  }
  // --- Fim Verificação Anti-Abuso ---

  // --- NOVO: Registrar atividade de streak se for ação qualificada ---
  if (STREAK_QUALIFIED_ACTIONS.includes(actionKey)) {
    try {
      await registerStreakActivity(supabase, userId, actionKey);
    } catch (err) {
      console.error('[processAction] Erro ao registrar streak:', err);
    }
  }

  // --- <<< NOVO: Verificar Multiplicador de Evento Especial ---
  let currentXpValue = actionData.xp_value;
  try {
    const eventMultiplier = await getActiveEventMultiplier(supabase, actionKey, userId); // Passa userId se o evento puder ser específico
    if (eventMultiplier > 1) {
      console.log(`[processAction] Aplicando multiplicador de evento (${eventMultiplier}x) para ação ${actionKey} para usuário ${userId}. XP original: ${currentXpValue}`);
      currentXpValue = Math.round(currentXpValue * eventMultiplier); // Arredonda para evitar decimais em XP
    }
    // --- NOVO: Aplicar multiplicador de streak ---
    const streakMultiplier = await getUserStreakXpMultiplier(supabase, userId);
    if (streakMultiplier > 1) {
      console.log(`[processAction] Aplicando multiplicador de streak (${streakMultiplier}x) para usuário ${userId}. XP antes: ${currentXpValue}`);
      currentXpValue = Math.round(currentXpValue * streakMultiplier);
    }
  } catch (eventError) {
    console.error(`[processAction] Erro ao verificar multiplicador de evento para ${actionKey}:`, eventError);
    // Continuar mesmo se houver erro na verificação do evento, usando o XP base
  }
  
  // --- Fim Verificação Multiplicador ---

  // --- Verificações de Cooldown e Limite Diário (Implementação Básica - pode ser otimizada) ---
  // Nota: Estas verificações podem precisar de otimizações (ex: cache) se chamadas muito frequentemente.
  // Serão implementadas de forma mais robusta se necessário em fases futuras.

  // Verificar cooldown (se aplicável)
  if (actionData.cooldown_minutes !== null && actionData.cooldown_minutes > 0) {
    try {
      const cooldownMinutes = actionData.cooldown_minutes;
      const now = new Date();
      const cooldownAgo = new Date(now.getTime() - cooldownMinutes * 60 * 1000);

      const { data: recentHistory, error: cooldownError } = await supabase
        .from('xp_history')
        .select('earned_at')
        .eq('user_id', userId)
        .eq('action_id', actionData.id) // Usa o ID da ação
        .gte('earned_at', cooldownAgo.toISOString())
        .order('earned_at', { ascending: false })
        .limit(1);

      if (cooldownError) {
        throw cooldownError; // Lança o erro para ser pego pelo catch
      }
      if (recentHistory && recentHistory.length > 0) {
        // Ação ainda está em cooldown
        const lastActionTime = new Date(recentHistory[0].earned_at);
        const remainingCooldownMs = cooldownAgo.getTime() + (cooldownMinutes * 60 * 1000) - now.getTime();
        
        // Formatar o tempo restante para uma mensagem mais amigável
        let remainingTimeString = "";
        const totalSeconds = Math.ceil(remainingCooldownMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        if (minutes > 0) {
          remainingTimeString += `${minutes} minuto(s)`;
        }
        if (seconds > 0) {
          if (minutes > 0) remainingTimeString += " e ";
          remainingTimeString += `${seconds} segundo(s)`;
        }
        if (!remainingTimeString) remainingTimeString = "alguns instantes"; // Fallback

        console.log(`[processAction] Ação ${actionKey} em cooldown para user ${userId}. Restam: ${remainingTimeString}`);
        return { 
          success: false, 
          error: `Ação em cooldown. Por favor, tente novamente em ${remainingTimeString}.`, 
          restriction: 'cooldown', 
          remainingTime: remainingCooldownMs 
        }; 
      }
    } catch (error) {
      console.error(`[processAction] Erro ao verificar cooldown para ${actionKey}:`, error);
      return { success: false, error: 'Erro ao verificar cooldown', restriction: 'check_failed' };
    }
  }

  // Verificar limite diário (se aplicável)
  if (actionData.max_daily !== null && actionData.max_daily > 0) {
    try {
      const todayStart = new Date();
      todayStart.setUTCHours(0, 0, 0, 0); // Início do dia em UTC

      const { count, error: dailyCountError } = await supabase
        .from('xp_history')
        .select('id', { count: 'exact', head: true }) // Apenas conta
        .eq('user_id', userId)
        .eq('action_id', actionData.id) // Usa o ID da ação
        .gte('earned_at', todayStart.toISOString());

      if (dailyCountError) {
        throw dailyCountError; // Lança o erro para ser pego pelo catch
      }
      if (count !== null && count >= actionData.max_daily) {
        console.log(`[processAction] Limite diário atingido para ${actionKey} por user ${userId}.`);
        return { success: false, error: 'Limite diário atingido', restriction: 'daily_limit' }; // <<< MUDANÇA: Retorno mais detalhado
      }
    } catch (error) {
      console.error(`[processAction] Erro ao verificar limite diário para ${actionKey}:`, error);
      return { success: false, error: 'Erro ao verificar limite diário', restriction: 'check_failed' };
    }
  }
  // --- Fim Verificações ---

  // 3. Chamar addUserXP para conceder os pontos (usando currentXpValue)
  const addUserXpResult = await addUserXP(
    supabase,
    userId,
    currentXpValue, // <<< MODIFICADO: usa o valor de XP potencialmente multiplicado
    'ACTION_REWARD',
    sourceId,
    actionData.id, // action_id da tabela xp_actions
    details
  );

  if (!addUserXpResult.success) {
    return { success: false, error: addUserXpResult.error?.message || 'Falha ao adicionar XP' };
  }

  let missionsUpdatedInfo: UserMissionUpdateInfo[] = []; // Renomeado e tipado corretamente

  // --- <<< NOVO: Chamar verificação de progresso de missões APÓS adicionar XP --- 
  try {
    // Passar supabase, userId, actionKey, e um objeto com sourceId e details (se disponíveis e relevantes para missões)
    const resultFromMissions = await processUserActionForMissions(supabase, userId, actionKey, { sourceId, ...details });
    if (resultFromMissions && resultFromMissions.length > 0) {
      missionsUpdatedInfo = resultFromMissions;
      console.log(`[processAction] Missões atualizadas/completas para ${userId} após ação ${actionKey}:`, missionsUpdatedInfo);
      // Se missões concedem XP/Badges, isso já deve ser tratado dentro de processUserActionForMissions ou funções chamadas por ele
    }
  } catch (missionError) {
    console.error(`[processAction] Erro ao atualizar progresso de missões para ${userId} após ação ${actionKey}:`, missionError);
    // Não falhar a ação principal por erro em missões, mas logar
  }
  // --- Fim da verificação de progresso de missões ---

  // --- <<< EXISTENTE: Chamar verificação de badges APÓS adicionar XP com sucesso --- 
  try {
    checkAndAwardAllPossibleBadges(userId);
  } catch (badgeError) {
    console.error(`[processAction] Erro assíncrono ao verificar badges para ${userId} após ação ${actionKey}:`, badgeError);
  }
  // --- Fim da verificação de badges ---

  // 4. Retornar resultado com informações relevantes
  // Garantimos que addUserXpResult.success é true aqui, então newTotalXp existe.
  const previousLevel = calculateLevel(addUserXpResult.newTotalXp! - currentXpValue); // <<< MODIFICADO: usa currentXpValue
  const newLevel = addUserXpResult.newLevel; 

  // Processar missões APÓS conceder XP pela ação primária
  if (processUserActionForMissions) {
    const resultFromMissions = await processUserActionForMissions(supabase, userId, actionKey, { sourceId, ...details });
    
    // Log adicionado aqui
    if (resultFromMissions && resultFromMissions.length > 0) {
      console.log('[GamificationService:processAction] ITEM 0 RECEBIDO:', JSON.stringify(resultFromMissions[0]));
    }

    console.log('[GamificationService:processAction] TIPO de resultFromMissions:', typeof resultFromMissions, 'É Array?', Array.isArray(resultFromMissions));
    console.log('[GamificationService:processAction] CONTEÚDO BRUTO de resultFromMissions:', JSON.stringify(resultFromMissions, null, 2));

    let missionUpdates: UserMissionUpdateInfo[] = [];

    if (Array.isArray(resultFromMissions)) {
      missionUpdates = resultFromMissions;
    } else {
      console.warn('[GamificationService:processAction] resultado de processUserActionForMissions NÃO É UM ARRAY!', resultFromMissions);
    }
    
    console.log('[GamificationService:processAction] missionUpdates (após verificação e atribuição):', JSON.stringify(missionUpdates, null, 2));

    if (missionUpdates && missionUpdates.length > 0) {
      missionsUpdatedInfo = missionUpdates;
    }
  }

  return {
    success: true,
    xpGained: currentXpValue, // <<< MODIFICADO: usa currentXpValue
    totalXP: addUserXpResult.newTotalXp,
    levelUp: !!newLevel && newLevel > previousLevel,
    missionsUpdated: missionsUpdatedInfo,
  };
}

// --- <<< NOVA FUNÇÃO: triggerUserMissionGeneration --- 
/**
 * Dispara a geração de missões diárias e semanais para um usuário.
 * Deve ser chamada, por exemplo, no login ou por um job.
 */
export async function triggerUserMissionGeneration(
  supabaseClient: SupabaseClient<Database>, // Renomeado para evitar conflito com a importação global 'supabase'
  userId: string
): Promise<{ success: boolean; dailyGenerated?: number; weeklyGenerated?: number; error?: string }> { // Ajustado o tipo de retorno
  if (!userId) {
    console.warn('[triggerUserMissionGeneration] User ID é obrigatório.');
    return { success: false, error: 'User ID é obrigatório.' };
  }
  try {
    const result = await generateUserMissions(supabaseClient, userId);
    console.log(`[triggerUserMissionGeneration] Geração de missões para usuário ${userId} concluída.`, result);
    return { 
      success: true, 
      dailyGenerated: result.dailyGenerated, 
      weeklyGenerated: result.weeklyGenerated 
    };
  } catch (error: any) {
    console.error(`[triggerUserMissionGeneration] Erro ao gerar missões para ${userId}:`, error);
    return { success: false, error: error.message || 'Erro desconhecido ao gerar missões.' };
  }
}
// --- Fim triggerUserMissionGeneration ---

// Outras funções do serviço serão adicionadas aqui nas próximas fases...
// Ex: checkBadges, awardBadge, etc. 

// --- Funções Auxiliares --- 

// Função auxiliar para buscar uma ação de XP pela chave
// (Assume que essas definições são carregadas de alguma forma, talvez um cache ou uma chamada inicial)
// Por simplicidade, vamos mockar um retorno por enquanto, idealmente isso viria do banco
let xpActionsCache: Map<XpActionKey, XpAction> | null = null;

async function loadXpActions(supabase: SupabaseClient<Database>): Promise<Map<XpActionKey, XpAction>> {
  if (xpActionsCache) return xpActionsCache;

  console.log('[Gamification] Loading XP Actions...');
  const { data, error } = await supabase.from('xp_actions').select('*').eq('is_active', true);

  if (error) {
    console.error('[Gamification] Error loading XP Actions:', error);
    return new Map();
  }

  xpActionsCache = new Map(data.map(action => [action.action_key as XpActionKey, action]));
  console.log(`[Gamification] Loaded ${xpActionsCache.size} active XP Actions.`);
  return xpActionsCache;
}

async function getXPAction(supabase: SupabaseClient<Database>, actionKey: XpActionKey): Promise<XpAction | null> {
  const cache = await loadXpActions(supabase);
  return cache.get(actionKey) || null;
}

/**
 * Busca o XP total de um usuário diretamente da tabela user_xp.
 * @param userId - O ID do usuário.
 * @returns O XP total ou 0 se não encontrado/erro.
 */
export async function getUserTotalXP(userId: string): Promise<number> {
  if (!userId) {
    console.error('[GamificationService:getUserTotalXP] userId é obrigatório.');
    return 0;
  }

  const { data, error } = await supabase
    .from('user_xp')
    .select('total_xp')
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGRST116') {
      console.error("[addUserXP] Erro ao buscar XP atual:", error);
  }
  return data?.total_xp || 0; 
}

// --- Funções Relacionadas a Badges (Implementação Inicial) ---

// Função para verificar se o usuário já possui uma badge
async function userHasBadge(supabase: SupabaseClient<Database>, userId: string, badgeId: string): Promise<boolean> {
   const { count, error } = await supabase
      .from('user_badges')
      .select('*' , { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('badge_id', badgeId);

  if (error) {
      console.error("[Gamification] Erro ao verificar se usuário tem badge:", error);
      return false; // Assumir que não tem em caso de erro
  }
  return (count || 0) > 0;
}

// Função para mapear ação para categorias de badge (exemplo)
function getBadgeCategoriesForAction(actionType: XpActionKey, actionData: any): string[] {
  // TODO: Implementar mapeamento de ações para categorias de badges relevantes
  // Ex: se actionType for 'create_post', retornar ['social']
  // Ex: se actionType for 'rate_game', retornar ['avaliacao', 'colecao']
  console.warn(`[Gamification] Mapeamento de ação ${actionType} para categorias de badge não implementado.`);
  return ['social', 'colecao', 'participacao']; // Retornar todas por enquanto para teste inicial
}

// --- Funções para obter status e histórico ---

/**
 * Busca o rank (posição) e o percentil de um usuário.
 */
export async function getUserRankAndPercentile(supabase: SupabaseClient<Database>, userId: string): Promise<{ rank_position: number | null; percentile: number | null }> {
  const { data, error } = await supabase.rpc('get_user_rank_and_percentile', {
    p_user_id: userId
  });

  if (error) {
    console.error("[Gamification] Erro ao buscar rank e percentil:", error);
    return { rank_position: null, percentile: null };
  }

  // A RPC retorna um array com um objeto, ou um array vazio se não encontrar
  const result = data?.[0]; 

  return {
    rank_position: result?.rank_position ?? null,
    percentile: result?.percentile ?? null
  };
}

/**
 * Busca o status de gamificação completo de um usuário (XP, nível, rank, posição, percentil).
 */
export async function getUserGamificationStatus(supabase: SupabaseClient<Database>, userId: string): Promise<UserXpData | null> {
  // 1. Busca os dados básicos da tabela user_xp
  const { data: xpData, error: xpError } = await supabase
    .from('user_xp')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (xpError && xpError.code !== 'PGRST116') { // Ignora erro 'not found'
    console.error("[Gamification] Erro ao buscar UserXpData:", xpError);
    return null;
  }

  if (!xpData) {
    return null; // Usuário sem registro de XP ainda
  }

  // 2. Busca o rank e percentil usando a função RPC
  const { rank_position, percentile } = await getUserRankAndPercentile(supabase, userId);

  // 3. Combina os resultados
  return {
    ...xpData,
    rank_position: rank_position,
    percentile: percentile,
  };
}

/**
 * Busca o histórico de XP de um usuário com paginação.
 */
export async function getXpHistory(
  supabase: SupabaseClient<Database>,
  userId: string,
  page = 1,
  pageSize = 20
): Promise<{ data: (XpHistoryEntry & { xp_actions: { action_key: string, description: string } | null })[]; count: number | null }> {
  const { data, error, count } = await supabase
    .from('xp_history')
    // Junta com xp_actions para obter a chave e descrição
    .select(`
      *,
      xp_actions ( action_key, description )
    `, { count: 'exact' })
    .eq('user_id', userId)
    .order('earned_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (error) {
    console.error("[Gamification] Erro ao buscar histórico de XP:", error);
    // Lançar erro ou retornar vazio?
    // Por consistência com outras funções, vamos lançar
    throw error; 
  }

  // O tipo de retorno precisa refletir a inclusão de xp_actions
  return { data: data || [], count };
} 

// NOVA FUNÇÃO PARA RESGATAR RECOMPENSAS DE MISSÃO
export async function redeemMissionReward(
  supabase: SupabaseClient,
  userId: string,
  userMissionId: string // ID da tabela user_missions
): Promise<{ success: boolean; error?: string; xp_granted?: number; badge_id_granted?: string; mission_title?: string; }> {
  try {
    // 1. Buscar a user_mission específica
    const { data: userMissionData, error: userMissionError } = await supabase // Renomeado para userMissionData
      .from('user_missions') 
      .select(`
        *,
        mission:missions(*)
      `)
      .eq('id', userMissionId)
      .eq('user_id', userId)
      .single();

    if (userMissionError) {
      console.error(`[GamificationService:redeemMissionReward] Erro ao buscar user_mission ${userMissionId} para usuário ${userId}:`, userMissionError);
      return { success: false, error: 'Erro ao buscar missão do usuário.' };
    }

    // Corrigir a tipagem de userMission aqui para corresponder ao que é realmente selecionado
    const userMission = userMissionData as (UserMissionRow & { mission: MissionRow | null }) | null;

    if (!userMission) {
      return { success: false, error: 'Missão do usuário não encontrada ou não pertence a este usuário.' };
    }

    // 2. Verificar se a missão está completa e a recompensa ainda não foi resgatada
    if (!userMission.is_complete) {
      return { success: false, error: 'A missão ainda não foi completada.' };
    }
    const signupRewardClaimedAt = (userMission as { is_rewarded?: boolean | null }).is_rewarded;
    if (signupRewardClaimedAt) {
      return { success: false, error: 'A recompensa para esta missão já foi resgatada.' };
    }

    // 3. Obter detalhes da recompensa do template da missão (já incluído no 'mission' do select)
    const missionTemplate = userMission.mission;
    if (!missionTemplate) {
      console.error(`[GamificationService:redeemMissionReward] Template da missão não encontrado para user_mission ${userMissionId}.`);
      return { success: false, error: 'Detalhes da recompensa da missão não encontrados.' };
    }

    let xpGranted = 0;
    let badgeIdGranted: string | undefined = undefined;

    // 4. Conceder XP, se houver
    if (missionTemplate.xp_reward && missionTemplate.xp_reward > 0) {
      const addUserXpResult = await addUserXP(
        supabase,
        userId,
        missionTemplate.xp_reward,
        'MISSION_REWARD',
        missionTemplate.id, // sourceId = mission_template_id
        undefined, 
        { mission_id: missionTemplate.id, mission_title: missionTemplate.title, user_mission_id: userMissionId } 
      );
      // addUserXP retorna { success: boolean; error?: any; newTotalXp?: number; newLevel?: number }
      // Não há xpGained diretamente no retorno de addUserXP, o valor concedido é missionTemplate.xp_reward
      if(addUserXpResult.success) {
        xpGranted = missionTemplate.xp_reward; 
      } else {
        // Tratar falha na concessão de XP, se necessário, ou apenas logar
        console.error(`[GamificationService:redeemMissionReward] Falha ao conceder XP para missão ${missionTemplate.id} (user_mission ${userMissionId}). Erro: ${addUserXpResult.error}`);
      }
    }

    // 5. Conceder Badge, se houver
    if (missionTemplate.badge_reward_id) {
      try {
        // Corrigido: awardBadgeToUser espera (userId: string, badgeId: string)
        // Os argumentos gamification_source_type e gamification_source_id foram removidos
        // Se badgeService.awardBadgeToUser foi atualizado para aceitá-los, esta chamada pode ser reajustada.
        await awardBadgeToUser(userId, missionTemplate.badge_reward_id);
        badgeIdGranted = missionTemplate.badge_reward_id;
      } catch (badgeError) {
        console.error(`[GamificationService:redeemMissionReward] Erro ao conceder badge ${missionTemplate.badge_reward_id} para missão ${missionTemplate.id} (user_mission ${userMissionId}):`, badgeError);
      }
    }

    // 6. Marcar a user_mission como recompensada
    const { error: updateError } = await supabase
      .from('user_missions')
      .update({ is_rewarded: true, updated_at: new Date().toISOString() })
      .eq('id', userMissionId);

    if (updateError) {
      console.error(`[GamificationService:redeemMissionReward] Erro ao marcar user_mission ${userMissionId} como recompensada:`, updateError);
      return { success: false, error: 'Erro ao finalizar o resgate da recompensa. Contate o suporte se o problema persistir.' };
    }

    return {
      success: true,
      xp_granted: xpGranted > 0 ? xpGranted : undefined,
      badge_id_granted: badgeIdGranted,
      mission_title: missionTemplate.title
    };

  } catch (error: any) {
    console.error(`[GamificationService:redeemMissionReward] Erro inesperado ao resgatar recompensa para user_mission ${userMissionId}:`, error);
    return { success: false, error: 'Ocorreu um erro inesperado.' };
  }
} 

// --- Função para o referente coletar a recompensa de indicação ---
/**
 * Permite que o usuário referente colete a recompensa de XP por indicação (cadastro do indicado).
 * @param supabase Instância do SupabaseClient
 * @param userId ID do usuário referente (quem indicou)
 * @param userReferralId ID do registro em user_referrals
 */
export async function claimReferralSignupReward(
  supabase: SupabaseClient<Database>,
  userId: string,
  userReferralId: string
): Promise<{ success: boolean; error?: string; xpGranted?: number }> {
  // 1. Buscar o registro de indicação
  const { data: referral, error: fetchError } = await supabase
    .from('user_referrals')
    .select('*')
    .eq('id', userReferralId)
    .single();

  if (fetchError) {
    return { success: false, error: 'Indicação não encontrada.' };
  }

  // 2. Verificar se o userId é o referrer correto
  if (referral.referrer_id !== userId) {
    return { success: false, error: 'Você não tem permissão para coletar esta recompensa.' };
  }

  // 3. Verificar se a recompensa já foi coletada
  const signupRewardClaimedAt = (referral as { signup_reward_claimed_at?: string | null }).signup_reward_claimed_at;
  if (signupRewardClaimedAt) {
    return { success: false, error: 'Recompensa já coletada.' };
  }

  // 4. Conceder XP ao referente
  const processResult = await processAction({
    supabase,
    userId,
    actionKey: 'referral_registration',
    sourceId: referral.referred_id,
    details: { referral_id: userReferralId, referred_id: referral.referred_id }
  });

  if (!processResult.success) {
    return { success: false, error: processResult.error || 'Erro ao conceder XP.' };
  }

  // 5. Atualizar o registro marcando como coletado
  const { error: updateError } = await supabase
    .from('user_referrals')
    .update({ signup_reward_claimed_at: new Date().toISOString() })
    .eq('id', userReferralId);

  if (updateError) {
    return { success: false, error: 'XP concedido, mas falha ao atualizar status da recompensa.' };
  }

  return { success: true, xpGranted: processResult.xpGained };
} 

// --- Função para buscar indicações pendentes de coleta de recompensa ---
export async function getPendingReferralRewards(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<{
  id: string;
  referred_id: string;
  referral_code_used: string | null;
  created_at: string;
  signup_reward_claimed_at: string | null;
  profiles?: { username?: string | null; full_name?: string | null; avatar_url?: string | null } | null;
}[]> {
  const { data, error } = await supabase
    .from('user_referrals')
    .select(`
      id,
      referred_id,
      referral_code_used,
      created_at,
      signup_reward_claimed_at,
      profiles!user_referrals_referred_id_fkey (username, full_name, avatar_url)
    `)
    .eq('referrer_id', userId)
    .is('signup_reward_claimed_at', null)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[getPendingReferralRewards] Erro ao buscar indicações pendentes:', error);
    return [];
  }
  if (!data) {
    return [];
  }
  return data;
} 

// --- NOVA FUNÇÃO PARA BUSCAR COOLDOWNS ATIVOS DO USUÁRIO ---
export async function getUserActiveCooldowns(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<ActiveCooldownInfo[]> {
  const now = new Date();

  // 1. Buscar todas as ações que possuem cooldown e estão ativas
  const { data: actionsWithCooldown, error: actionsError } = await supabase
    .from('xp_actions')
    .select('id, action_key, description, cooldown_minutes')
    .gt('cooldown_minutes', 0)
    .eq('is_active', true);

  if (actionsError) {
    console.error("[GamificationService:getUserActiveCooldowns] Erro ao buscar ações com cooldown:", actionsError);
    return [];
  }
  if (!actionsWithCooldown || actionsWithCooldown.length === 0) {
    return []; // Nenhuma ação configurada com cooldown
  }

  const activeCooldowns: ActiveCooldownInfo[] = [];

  // 2. Para cada ação, verificar a última vez que o usuário a executou
  for (const action of actionsWithCooldown) {
    if (action.id && action.cooldown_minutes && action.cooldown_minutes > 0) {
      const cooldownAgo = new Date(now.getTime() - action.cooldown_minutes * 60 * 1000);

      const { data: recentHistory, error: historyError } = await supabase
        .from('xp_history')
        .select('earned_at')
        .eq('user_id', userId)
        .eq('action_id', action.id)
        .gte('earned_at', cooldownAgo.toISOString()) // Verifica se ocorreu DENTRO da janela de cooldown
        .order('earned_at', { ascending: false })
        .limit(1);

      if (historyError) {
        console.error(`[GamificationService:getUserActiveCooldowns] Erro ao buscar histórico para ação ${action.action_key} (ID: ${action.id}):`, historyError);
        continue; // Pular para a próxima ação em caso de erro
      }

      if (recentHistory && recentHistory.length > 0) {
        const lastExecutedAt = new Date(recentHistory[0].earned_at);
        const cooldownExpiresAt = new Date(lastExecutedAt.getTime() + action.cooldown_minutes * 60 * 1000);

        // Ação está em cooldown se o tempo de expiração ainda não passou
        if (cooldownExpiresAt > now) {
          activeCooldowns.push({
            action_id: action.id,
            action_key: action.action_key,
            action_description: action.description || 'N/A',
            cooldown_minutes: action.cooldown_minutes,
            last_executed_at: lastExecutedAt.toISOString(),
            cooldown_expires_at: cooldownExpiresAt.toISOString(),
            remaining_time_ms: cooldownExpiresAt.getTime() - now.getTime(),
          });
        }
      }
    }
  }
  return activeCooldowns;
}
// --- FIM getUserActiveCooldowns ---

// --- Funções Auxiliares --- 

// Função auxiliar para buscar uma ação de XP pela chave
// ... existing code ...

// ... rest of the file remains unchanged ... 